import { dashboardApi, mgrApi, mgrPrivate, mgrPublic } from './client';

import type { PasswordPolicy } from '../lib/passwordPolicy';
import type { AvailableClient, Credentials, MfaSubmission, Session } from '../model/types';

export const createSession = (credentials: Credentials) =>
  mgrPrivate.post<Session>('/auth/sessions', credentials);

export const checkSsoDomain = (loginDomain: string) =>
  mgrPrivate.get<Session>('/auth/sessions', { params: { loginDomain } });

export const verifyMfaToken = (submission: MfaSubmission) =>
  mgrPrivate.post<Session>('/auth/sessions/verification-code', submission);

export const selectClient = (clientId: string) =>
  mgrPrivate.patch<Session>(`/auth/sessions/${clientId}`);

export const listAvailableClients = () => mgrApi.get<AvailableClient[]>('/users/clients');

export type PasswordResetChannel = 'EMAIL' | 'WHATSAPP';

export const requestPasswordReset = (email: string, channel: PasswordResetChannel = 'EMAIL') =>
  mgrPublic.post<void>('/users/reset', { email, channel });

export const verifyPasswordResetCode = (email: string, code: string) =>
  mgrPublic.post<{ recoveryToken: string }>('/users/reset/code', { email, code });

export const verifyAccountCode = (userId: string, code: string) =>
  mgrPublic.post<Session>('/users/code', { userId, code });

export const resendAccountCode = (userId: string, channel: 'EMAIL' | 'SMS') =>
  mgrPublic.put<void>('/users/code/resend', { userId, channel });

export const fetchPolicyByToken = (token: string) =>
  mgrPublic.get<Partial<PasswordPolicy>>(`/password-policies/token/${token}`);

export const fetchDefaultPolicy = () => mgrPublic.get<Partial<PasswordPolicy>>('/password-policies');

export const resetPassword = (payload: {
  recoveryToken: string;
  newPassword: string;
  repeatNewPassword: string;
}) => mgrPublic.put<void>(`/users/password?recoveryToken=${payload.recoveryToken}`, payload);

export const activateInvitedUser = (
  token: string,
  payload: { password: string; acceptedDataProcessing: boolean },
) => mgrPublic.post<void>(`/auth/signup/${token}`, payload);

export interface WorkspaceUserActivation {
  firstName: string;
  lastName: string;
  mobile: string;
  areaActivity: string;
  position: string;
  password: string;
  newPassword: string;
}

export const activateWorkspaceUser = (token: string, payload: WorkspaceUserActivation) =>
  mgrPublic.post<void>(`/users/token/${token}`, payload);

export const fetchLegalTemplateByToken = (token: string) =>
  mgrPublic.get<string>(`/auth/signup/${token}/legal`);

export const acceptLegalByToken = (token: string) =>
  mgrPublic.put<void>(`/auth/signup/${token}/legal`);

// This one lives on DASHBOARD_API_URL, not the manager host.
export const subscribeWorkspace = (workspaceId: string) =>
  dashboardApi.post<void>(`/user-workspaces/${workspaceId}`);
